import { useState, useEffect } from 'react';
import { Row, Col, Space, message } from 'antd';
import { ColumnProps } from "antd/es/table";
import SubjectConfig from "@/config/subject";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import ResponsiveTable from '@/components/responsiveTable';
import { ReModal, ReButton } from "@/components/refineElement";
import ModalSubject from './ModalSubject';
import { SubjectType } from './types';

const { getSubjects, createSubject, updateSubject, deleteSubject } = SubjectConfig;

function SubjectManagement() {
  const [subjectArray, setSubjectArray] = useState<SubjectType[]>([]);
  const [subjectInfo, setSubjectInfo] = useState({} as SubjectType);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const subjectT: any = getLangList("subject");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const okFunction = {
    create: createSubjectFunction,
    update: updateSubjectFunction,
  };

  const columns: ColumnProps<SubjectType>[] = [
    { title: subjectT.EN_NAME, dataIndex: "enName" },
    { title: subjectT.CN_NAME, dataIndex: "cnName" },
    { title: subjectT.SUBJECT_DESCRIPTION, dataIndex: "description" },
    {
      title: buttonT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => (
        <Space>
          {hasPermission('ORGANIZATION_SUBJECT_MANAGEMENT_EDIT') &&
            <ReButton
              iconName='tool'
              onClick={clickSubjectEdit(record)}
              attributeObject={{ type: 'default' }}
            >
              {buttonT.EDIT}
            </ReButton>
          }

          {hasPermission('ORGANIZATION_SUBJECT_MANAGEMENT_DELETE') &&
            <ReButton
              iconName='delete'
              onClick={clickSubjectDelete(record.subjectId)}
              attributeObject={{ type: 'danger' }}
            >
              {buttonT.DELETE}
            </ReButton>
          }
        </Space>
      ),
    }
  ];

  useEffect(() => {
    getSubjectsFunction();
  }, []);

  function getSubjectsFunction() {
    getSubjects().then((res: any) => {
      const subjectArray = res?.value ?? [];
      setSubjectArray(subjectArray);
    });
  };

  function clickSubjectCreate() {
    setSubjectInfo({} as SubjectType);
    toggleModal('create');
  };

  const clickSubjectEdit = (data: SubjectType) => (e: any) => {
    e.stopPropagation();
    setSubjectInfo(data);
    toggleModal('update');
  };

  const clickSubjectDelete = (id: number) => () => {
    showDelConfirm(() => deleteSubjectFunction(id));
  };

  function createSubjectFunction(values: SubjectType) {
    const { enName, cnName, description } = values;
    const params = { enName, cnName, description };

    createSubject(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSubjectsFunction();
    });
  };

  function updateSubjectFunction(values: SubjectType) {
    const { subjectId } = subjectInfo;
    const { enName, cnName, description } = values;
    const params = { subjectId, enName, cnName, description };

    if (!hasPermission('ORGANIZATION_SUBJECT_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateSubject(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSubjectsFunction();
    });
  }

  function deleteSubjectFunction(id: number) {
    const params = { subjectId: id };

    deleteSubject(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSubjectsFunction();
    });
  };

  function toggleModal(handleType: string = '') {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  return (
    <>
      <Row gutter={[0, 8]} justify="end">
        <Col>
          {hasPermission('ORGANIZATION_SUBJECT_MANAGEMENT_NEW') &&
            <ReButton iconName='plus' onClick={clickSubjectCreate}>
              {`${buttonT.NEW}${subjectT.SUBJECT}`}
            </ReButton>
          }
        </Col>

        <Col span={24}>
          <ResponsiveTable
            dataSource={subjectArray}
            columns={columns}
          />
        </Col>
      </Row>

      {isModalVisible &&
        <ReModal
          title={subjectT.SUBJECT}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
          CollectedForm={ModalSubject}
          formProps={{
            info: subjectInfo,
            handleType,
          }}
          width={'700px'}
        />
      }
    </>
  )
}

export default SubjectManagement;